import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import ModeEditOutlinedIcon from '@mui/icons-material/ModeEditOutlined';
import { useAppState } from 'components/contexts/State';
const { ThemeProvider } = require('@emotion/react');
const { defaultTheme } = require('theme');
function TabularDataStepTwo({ open, onClose, data, onSave, isLoading }) {
  const { currProcessId } = useAppState();
  const [rows, setRows] = useState([]);
  const [editingRowId, setEditingRowId] = useState(null);
  const [editingFeild, setEditingFeild] = useState(null);
  const [originalValue, setOriginalValue] = useState('');
  const [postTableData, setPostTableData] = useState({});
  const [columnNameErrors, setColumnNameErrors] = useState({});
  // Reset state when dialog opens with new data
  useEffect(() => {
    if (open && data?.table_data?.length > 0) {
      setPostTableData(data);
      setRows(data.table_data.map((row, index) => ({ ...row, id: index })));
      setEditingRowId(null);
      setEditingFeild(null);
      setOriginalValue('');
      setColumnNameErrors({});
    }
  }, [open, data]);
  const handleCheckboxChange = (id) => {
    setRows((prevRows) =>
      prevRows.map((row) => (row.id === id ? { ...row, include: !row.include } : row))
    );
  };
  const handleEditClick = (id, feild) => {
    const original = rows.find((row) => row.id === id)[feild];
    setOriginalValue(original);
    setEditingRowId(id);
    setEditingFeild(feild);
    setColumnNameErrors((prev) => {
      const { [id]: _, ...rest } = prev;
      return rest;
    });
  };
  // Inline error & revert logic for column name
  const handleValueChange = (id, field, newValue) => {
    if (field === 'updated_column_name') {
      const trimmedValue = newValue.trim();
      if (trimmedValue === '') {
        setRows((prevRows) =>
          prevRows.map((row) =>
            row.id === id ? { ...row, updated_column_name: originalValue } : row
          )
        );
        setEditingRowId(null);
        setEditingFeild(null);
        setColumnNameErrors((prev) => {
          const { [id]: _, ...rest } = prev;
          return rest;
        });
        return;
      }
      const isDuplicate = rows.some(
        (row) => row.updated_column_name.trim() === trimmedValue && row.id !== id
      );
      if (isDuplicate) {
        setColumnNameErrors((prev) => ({
          ...prev,
          [id]: 'Name already in use, please updates',
        }));
        return;
      }
      setRows((prevRows) =>
        prevRows.map((row) => (row.id === id ? { ...row, [field]: trimmedValue } : row))
      );
      setEditingRowId(null);
      setEditingFeild(null);
      setColumnNameErrors((prev) => {
        const { [id]: _, ...rest } = prev;
        return rest;
      });
      return;
    }
    setRows((prevRows) =>
      prevRows.map((row) => (row.id === id ? { ...row, [field]: newValue } : row))
    );
    setEditingRowId(null);
    setEditingFeild(null);
    setColumnNameErrors((prev) => {
      const { [id]: _, ...rest } = prev;
      return rest;
    });
  };
  const handleSendData = () => {
    const objToSend = { ...postTableData };
    objToSend.table_data = rows;
    objToSend.process_id = currProcessId;
    onSave(objToSend);
    setEditingRowId(null);
    setEditingFeild(null);
    setOriginalValue('');
    setColumnNameErrors({});
    setPostTableData({});
  };
  const handleClose = () => {
    if (!isLoading) {
      onClose();
      setEditingRowId(null);
      setEditingFeild(null);
      setOriginalValue('');
      setColumnNameErrors({});
      setPostTableData({});
    }
  };
  useEffect(() => {
    setColumnNameErrors((prevErrors) => {
      let nextErrors = { ...prevErrors };
      rows.forEach((row) => {
        // Only the currently edited row can have error
        if (row.id !== editingRowId) {
          if (nextErrors[row.id]) {
            const { [row.id]: _, ...rest } = nextErrors;
            nextErrors = rest;
          }
        } else {
          // If being edited, also remove if unique and non-blank
          const value = row.updated_column_name.trim();
          if (!value) {
            const { [row.id]: _, ...rest } = nextErrors;
            nextErrors = rest;
          } else {
            const isDuplicate = rows.some(
              (r) => r.updated_column_name.trim() === value && r.id !== row.id
            );
            if (!isDuplicate) {
              const { [row.id]: _, ...rest } = nextErrors;
              nextErrors = rest;
            }
          }
        }
      });
      return nextErrors;
    });
  }, [rows, editingRowId, editingFeild]);
  const isSaveDisabled =
    isLoading || Object.values(columnNameErrors).some((error) => !!error);
  const stepColumn = [
    {
      field: 'include',
      headerName: 'Include',
      minWidth: 97,
      maxWidth: 97,
      renderCell: (params) => {
        return (
          <Tooltip
            title={
              'Only include layers that are relevant to the data set. Please exclude any duplicate, extraneous, or irrelevant info.'
            }
          >
            <Checkbox
              checked={params.row.include}
              onChange={() => handleCheckboxChange(params.row.id)}
              sx={{ padding: 0, marginLeft: '13px' }}
            />
          </Tooltip>
        );
      },
      resizable: false,
    },
    {
      field: 'updated_column_name',
      headerName: 'Column Name',
      minWidth: 180,
      maxWidth: 180,
      flex: 1,
      renderCell: (params) => {
        const { id, updated_column_name } = params.row;
        const isEditing = editingRowId === id && editingFeild === 'updated_column_name';
        const errorText = columnNameErrors[id];
        return isEditing ? (
          <Box sx={{ width: '100%' }}>
            <TextField
              defaultValue={updated_column_name}
              size='small'
              autoFocus
              fullWidth
              variant='standard'
              error={Boolean(errorText)}
              onBlur={(e) => handleValueChange(id, 'updated_column_name', e.target.value)}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === 'Enter') {
                  handleValueChange(id, 'updated_column_name', e.target.value);
                }
              }}
              InputProps={{
                disableUnderline: true,
                sx: {
                  '&:before': { borderBottom: 'none !important' },
                  '&:after': { borderBottom: 'none !important' },
                  '&:hover:not(.Mui-disabled):before': {
                    borderBottom: 'none !important',
                  },
                  outline: 'none !important',
                  boxShadow: 'none !important',
                  border: errorText ? '1px solid #E14646' : '1px solid transparent',
                  borderRadius: '4px',
                  padding: '2px 6px',
                  background: errorText ? '#fff' : undefined,
                },
              }}
              sx={{
                width: '100%',
                '& .MuiInputBase-input': { padding: 0 },
                '& .Mui-focused': {
                  outline: 'none !important',
                  boxShadow: 'none !important',
                },
              }}
            />
            {errorText && (
              <Typography
                variant='caption'
                sx={{
                  color: '#E14646',
                  display: 'block',
                  mt: '2px',
                  ml: 0.5,
                  maxWidth: '100%',
                  whiteSpace: 'pre-line',
                  wordBreak: 'break-word',
                  fontSize: '12px',
                  width: '100%',
                }}
              >
                {errorText}
              </Typography>
            )}
          </Box>
        ) : (
          <Box
            sx={{
              maxWidth: '100%',
              overflow: 'hidden',
              whiteSpace: 'normal',
              wordWrap: 'break-word',
            }}
          >
            {updated_column_name}
            <IconButton
              size='small'
              onClick={() => handleEditClick(id, 'updated_column_name')}
              className='edit-icon'
              style={{ visibility: 'hidden', padding: 0 }}
            >
              <ModeEditOutlinedIcon
                fontSize='small'
                style={{ color: '#0064D6', fontSize: '14px', padding: 0 }}
              />
            </IconButton>
          </Box>
        );
      },
      resizable: false,
    },
    {
      field: 'description',
      headerName: 'Column Description',
      minWidth: 445,
      maxWidth: 445,
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        const { id, description } = params.row;
        return editingRowId === id && editingFeild === 'description' ? (
          <TextField
            defaultValue={description}
            size='small'
            autoFocus
            fullWidth
            onBlur={(e) => handleValueChange(id, 'description', e.target.value)}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === 'Enter') handleValueChange(id, 'description', e.target.value);
            }}
            variant='standard'
            InputProps={{
              disableUnderline: true,
              sx: {
                '&:before': { borderBottom: 'none !important' },
                '&:after': { borderBottom: 'none !important' },
                '&:hover:not(.Mui-disabled):before': { borderBottom: 'none !important' },
                outline: 'none !important',
                boxShadow: 'none !important',
              },
            }}
            sx={{
              '& .MuiInputBase-input': { padding: 0 },
              '& .Mui-focused': {
                outline: 'none !important',
                boxShadow: 'none !important',
              },
            }}
          />
        ) : (
          <Box
            sx={{
              maxWidth: '100%',
              overflow: 'hidden',
              whiteSpace: 'normal',
              wordWrap: 'break-word',
            }}
          >
            {description}
            <IconButton
              size='small'
              onClick={() => handleEditClick(id, 'description')}
              className='edit-icon'
              style={{ visibility: 'hidden' }}
            >
              <ModeEditOutlinedIcon
                fontSize='small'
                style={{ color: '#0064D6', fontSize: '14px' }}
              />
            </IconButton>
          </Box>
        );
      },
      resizable: false,
    },
  ];
  return (
    <ThemeProvider theme={defaultTheme}>
      <Dialog
        open={open}
        onClose={onClose}
        sx={{
          '.MuiDialog-paper': {
            width: '785px !important',
            maxWidth: '100%',
            boxShadow:
              '0px 11px 15px -7px rgba(0, 0, 0, 0.2), 0px 24px 38px 3px rgba(0, 0, 0, 0.14), 0px 9px 46px 8px rgba(0, 0, 0, 0.12)',
          },
        }}
      >
        <DialogTitle
          sx={{
            padding: '16px 24px',
            borderBottom: '1px solid rgba(0, 37, 71, 0.12)',
          }}
          className='text-heading-style-1'
        >
          Step 2 of 2: Edit column info
          <Typography
            variant='body2'
            gutterBottom
            sx={{ color: '#00254799', marginTop: '8px' }}
          >
            Please review and edit column names & descriptions below.
            <br />
            Duplicate layers are shown in gray (excluded). Layers with no data have been
            removed.
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ padding: '0px 24px !important' }}>
          <div style={{ height: '450px', overflow: 'auto' }}>
            <style>{`
            .MuiDataGrid-cell:hover .edit-icon{
              visibility:visible !important;
            }
            `}</style>
            <DataGrid
              rowHeight={32}
              rows={rows}
              columns={stepColumn}
              disableSelectionOnClick
              hideFooter
              disableColumnMenu
              getRowClassName={(params) =>
                params.row.include ? 'included-row' : 'excluded-row'
              }
              sx={{
                '& .included-row': { color: '#002547DE !important' },
                '& .excluded-row': { color: '#00254761 !important' },
                '& .MuiDataGrid-columnHeaderTitle': {
                  color: '#002547DE',
                },
                '& .MuiDataGrid-cell': {
                  padding: '8px',
                  lineHeight: '18px',
                },
                '& .MuiDataGrid-columnHeaders': {
                  position: 'sticky',
                  top: 0,
                  zIndex: 1000,
                  backgroundColor: 'white',
                },
                '& .Mui-selected': {
                  backgroundColor: 'inherit !important',
                },
                '& .MuiDataGrid-virtualScrollerContent': {
                  flexBasis: 'auto !important',
                },
              }}
            />
          </div>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button
            className='primary-text-button-medium'
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            variant='contained'
            className='primary-contained-button-medium'
            onClick={handleSendData}
            disabled={isSaveDisabled}
            endIcon={isLoading ? <CircularProgress size={12} /> : null}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
}
export default TabularDataStepTwo;
